import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import SupplierForm from "@/v2/components/pages/supplier/Form";
import { Mail } from "lucide-react";
import logo from "@/assets/logo.svg";
import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import LinkValidation from "@/v2/api/supplier/LinkValidation";
import { Toaster } from "sonner";
import { useState } from "react";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import InvalidTokenMsg from "@/v2/components/pages/supplier/InvalidTokenMsg";
import ThankYouMsg from "@/v2/components/pages/supplier/ThankYouMsg";

export default function OnboardSupplierPage() {
    const { id } = useParams();

    const [submitted, setSubmitted] = useState(false);

    if (!id) {
        return <Navigate to={"/login"} />;
    }

    const query = useQuery({
        queryKey: ["suppliers", id],
        queryFn: () => LinkValidation(id!),
        enabled: !!id,
    });

    if (query.isLoading)
        return (
            <div className="flex min-h-screen w-full items-center justify-center">
                <LoadingAnimation />
            </div>
        );

    return (
        <div className="flex min-h-screen w-screen flex-col bg-gradient-to-br from-blue-50 via-indigo-50/40 to-purple-50 dark:from-slate-950 dark:via-blue-950/30 dark:to-slate-900">
            {/* Top Navigation Bar */}
            <Toaster richColors position="top-center" />
            <div className="sticky top-0 z-50 border-b border-blue-100 bg-white/90 shadow-sm backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/90">
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        {/* Logo & Company Name */}
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <img src={logo} className="h-10" />
                            </div>
                            <div>
                                <h1 className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-xl font-bold text-primary">
                                    AR Engineering
                                </h1>
                            </div>
                        </div>

                        {/* Contact Button */}
                        <button className="hidden items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:scale-105 hover:bg-blue-700 hover:shadow-lg sm:flex">
                            <Mail className="h-4 w-4" />
                            Contact Us
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
                {/* Right Column - Registration Form */}
                <div className="lg:col-span-8">
                    <Card className="border-blue-200/60 bg-white/90 shadow-2xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/90">
                        <CardHeader className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50/30 pb-6 dark:border-slate-800 dark:from-slate-800/50 dark:to-slate-800/30">
                            <div className="flex items-start justify-between">
                                <div>
                                    <CardTitle className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
                                        Supplier Registration
                                    </CardTitle>
                                    <CardDescription className="mt-2.5 text-base">
                                        Complete the form below to join our
                                        supplier network.
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pb-8 pt-8">
                            {query.isError ? (
                                <InvalidTokenMsg />
                            ) : submitted ? (
                                <ThankYouMsg />
                            ) : (
                                <SupplierForm
                                    token={id}
                                    setSubmitted={setSubmitted}
                                />
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Footer */}
                <div className="mt-12 space-y-2 pb-8 text-center">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        By registering, you agree to our{" "}
                        <button className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400">
                            Terms of Service
                        </button>{" "}
                        and{" "}
                        <button className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400">
                            Privacy Policy
                        </button>
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-500">
                        © 2025 AR Engineering KSA. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
}
